import { NUMBER_SERIES, type NumberSeries } from './kinds.ts';

/** Префиксы номеров по сериям (раздел 17): `З-2026-0001`, `СЧ-2026-0001`, `А-…`, `Д-…`, `АВ-…`. */
export type NumberPrefixes = Readonly<Record<NumberSeries, string>>;

export const DEFAULT_PREFIXES: NumberPrefixes = {
  order: 'З',
  invoice: 'СЧ',
  act: 'А',
  employee_contract: 'Д',
  payout_act: 'АВ',
};

/**
 * Настройки документов — одна строка: префиксы номеров, срок ссылки на подпись, срок кода, попытки, пауза перед
 * повторной отправкой кода и «Подпись заявки выигрывает сделку».
 */
export interface DocumentSettings {
  readonly prefixes: NumberPrefixes;
  readonly linkValidDays: number;
  readonly codeMinutes: number;
  readonly codeAttempts: number;
  readonly resendSeconds: number;
  readonly winOnSign: boolean;
}

export const SETTINGS_LIMITS = {
  linkValidDays: { min: 1, max: 90 },
  codeMinutes: { min: 1, max: 60 },
  codeAttempts: { min: 1, max: 10 },
  resendSeconds: { min: 10, max: 600 },
} as const;

export type DocumentSettingsIssue =
  'prefix_invalid' | 'prefix_taken' | 'link_valid_days' | 'code_minutes' | 'code_attempts' | 'resend_seconds';

/** Префикс — буквы и цифры, до 6 знаков: номер `ПРЕФИКС-2026-0001` читается и печатается без сюрпризов. */
export const PREFIX_PATTERN = /^[0-9A-Za-zА-ЯЁа-яё]{1,6}$/u;

/** Префикс без пробелов по краям, в верхнем регистре: «сч» → «СЧ». */
export const normalizePrefix = (prefix: string): string => prefix.trim().toUpperCase();

const within = (value: number, limit: { readonly min: number; readonly max: number }): boolean =>
  Number.isInteger(value) && value >= limit.min && value <= limit.max;

/** Проблемы настроек по порядку полей; одинаковые префиксы дали бы два документа с одним номером. */
export function documentSettingsIssues(settings: DocumentSettings): DocumentSettingsIssue[] {
  const issues: DocumentSettingsIssue[] = [];
  const prefixes = NUMBER_SERIES.map((series) => settings.prefixes[series]);
  if (prefixes.some((prefix) => !PREFIX_PATTERN.test(prefix))) issues.push('prefix_invalid');
  else if (new Set(prefixes).size !== prefixes.length) issues.push('prefix_taken');
  if (!within(settings.linkValidDays, SETTINGS_LIMITS.linkValidDays)) issues.push('link_valid_days');
  if (!within(settings.codeMinutes, SETTINGS_LIMITS.codeMinutes)) issues.push('code_minutes');
  if (!within(settings.codeAttempts, SETTINGS_LIMITS.codeAttempts)) issues.push('code_attempts');
  if (!within(settings.resendSeconds, SETTINGS_LIMITS.resendSeconds)) issues.push('resend_seconds');
  return issues;
}

/** Номер документа серии: `З-2026-0012`; после 9999 номер просто становится длиннее. */
export function documentNumber(prefix: string, year: number, n: number): string {
  if (!Number.isSafeInteger(n) || n < 1) throw new RangeError(`Номер документа: ${String(n)}`);
  return `${prefix}-${String(year)}-${String(n).padStart(4, '0')}`;
}
